import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, LinearProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachmentCaptionRow from './AttachmentCaptionRow';

const AttachmentsPanel = ({
  title = 'Files',
  emptyText = 'No files yet.',
  attachments = [],
  onUpload,
  onSaveCaption,
  onDelete,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const onDrop = async (accepted) => {
    if (!accepted?.length) return;
    setUploading(true);
    setUploadError('');
    try {
      for (let i = 0; i < accepted.length; i++) {
        await onUpload(accepted[i]);
      }
    } catch (error) {
      setUploadError((error && error.message) || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  return (
    <Box sx={{ p: 1.5, bgcolor: '#fafafa', borderRadius: 1 }}>
      {title ? <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography> : null}

      <Box sx={{
        display: 'flex', gap: 1.5, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' },
      }}
      >
        <Box sx={{ width: { xs: '100%', sm: '25%' }, flexShrink: 0 }}>
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 1,
              p: 1.5,
              minHeight: 88,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              textAlign: 'center',
              cursor: 'pointer',
              color: '#777',
              backgroundColor: isDragActive ? '#f0f8ff' : '#fff',
              '&:hover': { backgroundColor: '#f0f8ff' },
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon fontSize="small" />
            <Typography variant="caption">
              {isDragActive ? 'Drop here' : 'Drag & drop or click to upload'}
            </Typography>
          </Box>
          {uploading && <LinearProgress sx={{ mt: 1 }} />}
          {uploadError ? (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              {uploadError}
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', sm: '75%' } }}>
          {attachments.length === 0 ? (
            <Typography variant="caption" color="textSecondary">{emptyText}</Typography>
          ) : (
            attachments.map(a => (
              <AttachmentCaptionRow
                key={a.id}
                attachment={a}
                onSaveCaption={caption => onSaveCaption(a, caption)}
                onDelete={() => onDelete(a)}
              />
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AttachmentsPanel;
